/**
 * Opening a checkout — **Razorpay Checkout in the page, or a hosted page by
 * navigation**. FR-G7, ADR-0027 §2; Wave 11A (T-3.15, T-3.16).
 *
 * One module for both surfaces. The staff invoice screen and the portal's
 * billing screen draw the same thing from the same `descriptor` (contract
 * §4.1), and two copies of *"how a Razorpay order becomes a checkout"* would
 * agree today and disagree the first time either is touched. It imports
 * nothing, so the portal bundle takes it without taking anything of the staff
 * app's — the same reason `portal/` imports nothing else from `src/`.
 *
 * ## What the success callback does: nothing, on purpose
 *
 * Checkout's `handler` fires in the payer's browser with a payment id, an
 * order id and a signature. **It writes nothing.** A receipt is created only
 * from `integration.payment.captured`, which the platform publishes after
 * verifying Razorpay's signed webhook (ADR-0027 §2, §4; contract §4.2: *"the
 * return URL is a hint"*). A browser callback is forgeable; a signed,
 * deduplicated webhook is not. So the callback's whole job is a sentence —
 * `PAID_NOTICE` — and the screen re-reads in a moment.
 *
 * ## The script is loaded on demand, and only here
 *
 * `checkout.js` is the first third-party script either surface serves
 * (ADR-0027, Consequences). It is inserted the first time somebody presses
 * **Pay**, never at boot, so a screen that never pays never fetches it. The
 * portal's CSP allows exactly its origin and the frames Checkout opens
 * (`server/portal.ts: PORTAL_HEADERS`); the staff app sets no CSP.
 */

export const CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js'

/** What the payer is told when Checkout reports success. ADR-0027 §2's words:
 *  the money is not on the ledger until the event says so. */
export const PAID_NOTICE =
  'Payment received — it will show here in a moment, once the gateway confirms it.'

/** `descriptor` as the contract names it (§4.1). `{}` when there is none. */
export type CheckoutDescriptor =
  | { kind: 'razorpay_order'; order_id: string; key_id: string }
  | { kind: 'hosted_url'; url: string }
  | { kind?: undefined }

/** The fields both surfaces' `payment-session` routes serve for drawing a
 *  checkout. The service fills `prefill` in Checkout's own spelling. */
export interface CheckoutSession {
  available: boolean
  descriptor: CheckoutDescriptor | Record<string, unknown>
  firm_name?: string
  description?: string
  prefill?: { name?: string; email?: string; contact?: string }
}

export interface CheckoutCallbacks {
  /** Checkout said the payment succeeded. Say `PAID_NOTICE`; write nothing. */
  onPaid: () => void
  /** The payer closed Checkout without paying. */
  onDismiss?: () => void
}

/** The subset of Razorpay's constructor this module uses. */
export interface RazorpayInstance {
  open: () => void
}
export type RazorpayConstructor = new (options: RazorpayOptions) => RazorpayInstance

/** Razorpay Checkout's documented options — only the ones this app sets. No
 *  `amount` and no `currency`: an `order_id` carries both, and a second copy
 *  here is a second number that could disagree with the order. */
export interface RazorpayOptions {
  key: string
  order_id: string
  name: string
  description?: string
  prefill?: { name?: string; email?: string; contact?: string }
  handler: (response: unknown) => void
  modal: { ondismiss: () => void }
}

export type CheckoutOutcome = 'opened' | 'navigated' | 'unavailable'

function isOrder(
  d: CheckoutSession['descriptor'],
): d is { kind: 'razorpay_order'; order_id: string; key_id: string } {
  return (
    d.kind === 'razorpay_order' &&
    typeof (d as { order_id?: unknown }).order_id === 'string' &&
    typeof (d as { key_id?: unknown }).key_id === 'string'
  )
}

function isHosted(d: CheckoutSession['descriptor']): d is { kind: 'hosted_url'; url: string } {
  const url = (d as { url?: unknown }).url
  return d.kind === 'hosted_url' && typeof url === 'string' && /^https:\/\//.test(url)
}

/**
 * Checkout's options for one session. Pure, so the one thing that matters —
 * **the handler writes nothing** — is testable without a browser.
 */
export function checkoutOptions(
  session: CheckoutSession,
  callbacks: CheckoutCallbacks,
): RazorpayOptions | null {
  const d = session.descriptor
  if (!isOrder(d)) return null
  const prefill = session.prefill && Object.keys(session.prefill).length > 0 ? session.prefill : undefined
  return {
    key: d.key_id,
    order_id: d.order_id,
    // The firm's name as printed on the invoice, so the payer sees who they are
    // paying. Never "ProDesk": the fee is the firm's.
    name: session.firm_name || 'Payment',
    ...(session.description ? { description: session.description } : {}),
    ...(prefill ? { prefill } : {}),
    // The response carries `razorpay_payment_id`, `razorpay_order_id` and
    // `razorpay_signature`. It is deliberately not read and not sent anywhere.
    handler: () => callbacks.onPaid(),
    modal: { ondismiss: () => callbacks.onDismiss?.() },
  }
}

let loading: Promise<RazorpayConstructor> | null = null

/**
 * Insert `checkout.js` once and resolve `window.Razorpay`.
 *
 * Memoised, so two presses load it once; **forgotten on failure**, so a press
 * on a train that lost signal can be retried rather than failing for the life
 * of the tab.
 */
export function loadCheckout(
  doc: Document = document,
  win: Window & { Razorpay?: RazorpayConstructor } = window,
): Promise<RazorpayConstructor> {
  if (win.Razorpay) return Promise.resolve(win.Razorpay)
  if (loading) return loading
  loading = new Promise<RazorpayConstructor>((resolve, reject) => {
    const script = doc.createElement('script')
    script.src = CHECKOUT_SCRIPT
    script.async = true
    script.onload = () => {
      if (win.Razorpay) resolve(win.Razorpay)
      else reject(new Error('Razorpay Checkout did not load.'))
    }
    script.onerror = () => {
      script.remove()
      reject(new Error('Razorpay Checkout could not be reached.'))
    }
    doc.head.append(script)
  }).catch((err: unknown) => {
    loading = null
    throw err
  })
  return loading
}

/** Tests only: forget a memoised load. */
export function resetCheckoutLoader(): void {
  loading = null
}

export interface CheckoutDeps {
  load?: () => Promise<RazorpayConstructor>
  navigate?: (url: string) => void
}

/**
 * Draw the checkout a session describes.
 *
 * `razorpay_order` → load `checkout.js` and open it in the page;
 * `hosted_url` → go there (https only); anything else → `'unavailable'`, and
 * the caller shows the session's `reason`. Rejects only if the script cannot
 * be loaded — which the caller turns into a sentence.
 */
export async function openCheckout(
  session: CheckoutSession,
  callbacks: CheckoutCallbacks,
  deps: CheckoutDeps = {},
): Promise<CheckoutOutcome> {
  if (!session.available) return 'unavailable'
  const d = session.descriptor
  if (isHosted(d)) {
    ;(deps.navigate ?? ((url: string) => window.location.assign(url)))(d.url)
    return 'navigated'
  }
  const options = checkoutOptions(session, callbacks)
  if (!options) return 'unavailable'
  const Razorpay = await (deps.load ?? (() => loadCheckout()))()
  new Razorpay(options).open()
  return 'opened'
}
